import type { ContentRevisionReason } from "@a7y/types";

/** The shape every section's revision satisfies — `ContentRevisionBase<TDoc>`
 *  (`BlogRevision`, `DocPageRevision`, …) minus the fields these helpers
 *  don't touch. */
type ReasonedRevision = { reason: ContentRevisionReason; createdAt: string };

/**
 * Pure revision-selection helpers for the Publishing queue (A7Y-4474 D15;
 * generalised to every content section at A7Y-4470 — generic over any
 * `ContentRevisionBase`-shaped revision, not just `BlogRevision`, so the
 * same functions serve docs without a second, re-derived copy). Revisions
 * are the only durable record of "what a document looked like before", so
 * both queue actions below are just "the newest snapshot written for the
 * right reason" — never index 0, since revisions are not guaranteed to
 * arrive already sorted newest-first.
 */
function newestByReason<T extends ReasonedRevision>(
  revisions: readonly T[],
  reason: ContentRevisionReason,
): T | undefined {
  return revisions
    .filter((r) => r.reason === reason)
    .reduce<T | undefined>((newest, r) => {
      if (!newest) return r;
      return Date.parse(r.createdAt) > Date.parse(newest.createdAt) ? r : newest;
    }, undefined);
}

/**
 * "Discard edits" on a `edits` queue row (unpublished changes on a live
 * document): restores the newest `publish` revision, undoing everything
 * since the document last actually went live.
 */
export function pickDiscardRevision<T extends ReasonedRevision>(
  revisions: readonly T[],
): T | undefined {
  return newestByReason(revisions, "publish");
}

/**
 * The revision a `draft, from archived` status-control transition restores:
 * the newest `archive` snapshot, i.e. the document as it was the moment it
 * was taken down.
 */
export function pickRestoreRevision<T extends ReasonedRevision>(
  revisions: readonly T[],
): T | undefined {
  return newestByReason(revisions, "archive");
}
